import React, { useEffect, useState, useCallback } from 'react';
import {
  EvaluationItem,
  EVALUATION_TYPE_LABELS,
  EVALUATION_STATUS_LABELS,
} from '../../../shared/hooks/useEvaluations';
import { ProbationEvaluationForm, isProbationType } from '../../../shared/components/ProbationEvaluationForm';
import { WorkEvaluationContent } from '../../../shared/components/WorkEvaluationContent';

interface EmployeeEvaluationHistoryPanelProps {
  employeeId: string;
  employeeName: string;
  evaluations: EvaluationItem[];
  loading: boolean;
  onClose: () => void;
  onLoadHistory: (targetUserId: string) => Promise<void>;
  onLoadDetail: (evaluationId: string) => Promise<EvaluationItem | null>;
}

export function EmployeeEvaluationHistoryPanel({
  employeeId,
  employeeName,
  evaluations,
  loading,
  onClose,
  onLoadHistory,
  onLoadDetail,
}: EmployeeEvaluationHistoryPanelProps) {
  const [detailEval, setDetailEval] = useState<EvaluationItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    onLoadHistory(employeeId);
    setDetailEval(null);
  }, [employeeId]);

  const handleItemClick = useCallback(async (evalId: string) => {
    setDetailLoading(true);
    const detail = await onLoadDetail(evalId);
    setDetailEval(detail);
    setDetailLoading(false);
  }, [onLoadDetail]);

  const handleBack = useCallback(() => {
    setDetailEval(null);
  }, []);

  return (
    <>
      {/* backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.3)', zIndex: 999,
        }}
      />
      {/* panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '60%', maxWidth: 900, minWidth: 400,
        background: 'white', boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
        padding: 24, overflow: 'auto', zIndex: 1000,
      }}>
        {/* header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {detailEval && (
              <button
                onClick={handleBack}
                style={{
                  border: 'none', background: 'none', fontSize: 18,
                  cursor: 'pointer', padding: '0 4px', color: '#374151',
                }}
              >
                ←
              </button>
            )}
            <h3 style={{ margin: 0 }}>
              {detailEval
                ? `${employeeName} — ${EVALUATION_TYPE_LABELS[detailEval.templateType] || detailEval.templateType}`
                : `${employeeName} 평가 이력`}
            </h3>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {/* content */}
        {detailEval ? (
          // 상세 보기
          <DetailView evaluation={detailEval} loading={detailLoading} />
        ) : (
          // 목록
          <ListView
            evaluations={evaluations}
            loading={loading}
            onItemClick={handleItemClick}
          />
        )}
      </div>
    </>
  );
}

// ============================================
// ListView — 완료된 평가 목록
// ============================================

function ListView({
  evaluations,
  loading,
  onItemClick,
}: {
  evaluations: EvaluationItem[];
  loading: boolean;
  onItemClick: (id: string) => void;
}) {
  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>불러오는 중...</div>;
  }

  if (evaluations.length === 0) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>완료된 평가가 없습니다.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {evaluations.map(ev => (
        <div
          key={ev.id}
          onClick={() => onItemClick(ev.id)}
          style={{
            padding: '14px 16px', borderRadius: 8,
            border: '1px solid #E5E7EB', cursor: 'pointer',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#F9FAFB'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'white'; }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                {EVALUATION_TYPE_LABELS[ev.templateType] || ev.templateType}
              </div>
              <div style={{ fontSize: 13, color: '#6B7280' }}>
                {ev.evaluationYear}년 · {EVALUATION_STATUS_LABELS[ev.status] || ev.status}
                {ev.finalGrade && ` · 최종등급: ${ev.finalGrade}`}
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF' }}>
              {ev.createdAt?.slice(0, 10) || '-'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================
// DetailView — 평가 상세 (ProbationEvaluationForm readonly 재사용)
// ============================================

function DetailView({
  evaluation,
  loading,
}: {
  evaluation: EvaluationItem;
  loading: boolean;
}) {
  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>불러오는 중...</div>;
  }

  // 기본 정보 카드
  const infoCard = (
    <div style={{ background: '#F9FAFB', padding: 16, borderRadius: 8, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 14 }}>
        <div><span style={{ color: '#666' }}>유형:</span> {EVALUATION_TYPE_LABELS[evaluation.templateType] || evaluation.templateType}</div>
        <div><span style={{ color: '#666' }}>상태:</span> {EVALUATION_STATUS_LABELS[evaluation.status] || evaluation.status}</div>
        <div><span style={{ color: '#666' }}>연도:</span> {evaluation.evaluationYear}년</div>
        {evaluation.evaluationPeriod && (
          <div><span style={{ color: '#666' }}>기간:</span> {evaluation.evaluationPeriod}</div>
        )}
        {evaluation.finalGrade && (
          <div><span style={{ color: '#666' }}>최종등급:</span> {evaluation.finalGrade}</div>
        )}
        {evaluation.evaluatorName && (
          <div><span style={{ color: '#666' }}>평가자:</span> {evaluation.evaluatorName}</div>
        )}
        {evaluation.leadEvaluatorName && (
          <div><span style={{ color: '#666' }}>리드:</span> {evaluation.leadEvaluatorName}</div>
        )}
      </div>
    </div>
  );

  // V60: 평가 타입에 따라 올바른 양식 렌더링
  if (evaluation.templateNodes && evaluation.templateItems && evaluation.templateScoringScale) {
    return (
      <>
        {infoCard}
        {isProbationType(evaluation.templateType) ? (
          <ProbationEvaluationForm
            mode="readonly"
            nodes={evaluation.templateNodes}
            items={evaluation.templateItems}
            scoringScale={evaluation.templateScoringScale}
            templateType={evaluation.templateType}
            selfEvaluation={evaluation.selfEvaluation}
            leadEvaluation={evaluation.leadEvaluation || undefined}
            managerEvaluation={evaluation.managerEvaluation || undefined}
            formData={{}}
            selfTotals={evaluation.selfTotals}
            managerTotals={evaluation.managerTotals}
            attendanceSummary={evaluation.attendanceSummary}
          />
        ) : (
          <WorkEvaluationContent
            evaluation={evaluation}
            formData={evaluation.managerEvaluation as Record<string, any> || {}}
          />
        )}
        {/* 코멘트 */}
        <CommentsSection evaluation={evaluation} />
      </>
    );
  }

  // 템플릿 데이터 없는 경우 (레거시) — 기본 정보만 표시
  return (
    <>
      {infoCard}
      <div style={{ padding: 16, color: '#9CA3AF', textAlign: 'center' }}>
        상세 평가 양식을 표시할 수 없습니다.
      </div>
      <CommentsSection evaluation={evaluation} />
    </>
  );
}

// ============================================
// CommentsSection — 코멘트 표시
// ============================================

function CommentsSection({ evaluation }: { evaluation: EvaluationItem }) {
  const comments = [
    { label: '자가평가 의견', value: evaluation.selfComment, date: evaluation.selfSubmittedAt },
    { label: '리드평가 의견', value: evaluation.leadComment, date: evaluation.leadSubmittedAt },
    { label: '최종평가 의견', value: evaluation.managerComment, date: evaluation.managerSubmittedAt },
    { label: '종합 의견', value: evaluation.finalComment },
  ].filter(c => c.value);

  if (comments.length === 0) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>평가 의견</h4>
      {comments.map((c, i) => (
        <div key={i} style={{ marginBottom: 12, padding: 12, background: '#F9FAFB', borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
            {c.label} {c.date && `(${c.date.slice(0, 10)})`}
          </div>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}
